import React, {useState, useEffect} from 'react';
import './style.scss';

import { Filter, ItemSort } from '../../types';
import Select from 'react-select';
import i18n from 'live/services/i18n';
import styled from 'styled-components';
import { useEventEmitter } from 'ahooks';
import { EventEmitter } from 'ahooks/lib/useEventEmitter';
import FilterDropdown from 'live/components/forms/ui/filter-dropdown';
import ButtonWithLoading from 'live/components/forms/ui/button-with-loading'
 
type Props = {
    filters: Filter[]
    values: Event[],
    optionsInitialApplied?:any
    apply(values: any)
}

type EventEmited = {
    reset?: boolean,    
    filtersSelected?: any
}


const DropdownFilter = ({values, filters, optionsInitialApplied = {}, apply}: Props): JSX.Element => {
    const action$ = useEventEmitter();
    const [btnDisabled, setBtnDisabled] = useState<any>(true);
    const [optionsApplied, setOptionsApplied] = useState<any>(optionsInitialApplied);
    const [optionsSelected, setOptionsSelected] = useState<any>({});

    const removeFilters = () => {    
        setOptionsSelected({})    
        setOptionsApplied({})
        apply({})
        action$.emit({'reset': true})
    }

    const applyFilters = () => {                
        apply(optionsSelected)
        setOptionsApplied(optionsSelected)
    }

    const isBtnDisable = (values) => {
        let isDisabled = true;

        for(let key in values){
            const aux = values[key]

            if(aux && aux?.length > 0) {
                isDisabled  = false
                break;
            }
        }

        return isDisabled
    }

    const dropDownHide = () => {
        setBtnDisabled(isBtnDisable(optionsApplied))
    }

    action$.useSubscription(({filtersSelected}: EventEmited ) => {
        if(filtersSelected){            
            setOptionsSelected(filtersSelected)
            setBtnDisabled(isBtnDisable(filtersSelected))
        }
    });

    useEffect(() => {        
        setOptionsApplied({...optionsInitialApplied})
        setOptionsSelected({...optionsInitialApplied})
    }, [optionsInitialApplied])

    return <FilterDropdown hideTitle={true} onHide={dropDownHide} disableTooltip={true}
                containerClassName="smart-mobility-dropdown-filters"        
                btnLabel={
                    <BtnLabel className="dropdown-toggle btn btn-link">
                        <i className={`${Object.keys(optionsApplied)?.length > 0 ? 'fas' : 'far'} fa-filter`}></i>                       
                    </BtnLabel>
                }>
                
                <Wrapper>
                    <ListSelect initialOptions={optionsApplied} filters={filters} values={values} action$={action$}/>                   

                    <WrappperActions>
                        <ButtonWithLoading
                            disabled={btnDisabled}
                            buttonMessage=''
                            buttonMessageLoading=''
                            className="btn-sm btn-bold dropdown-filter-btn-item"
                            iconSaveButton={false}
                            onClick={applyFilters}
                            typeOfComponentToSave="Apply"
                        />

                        <RemoveFilters                      
                            disabled={btnDisabled}
                            buttonMessage=''
                            buttonMessageLoading=''
                            className="btn-sm btn-bold dropdown-filter-btn-item"
                            iconSaveButton={false}
                            onClick={removeFilters}
                            typeOfComponentToSave={i18n('Remove filters')}
                        />
                    </WrappperActions>
                </Wrapper>
            </FilterDropdown>
}

export default DropdownFilter;

type ListSelectProps = {
    filters: Filter[]
    values: Event[]
    initialOptions: any
    action$:  EventEmitter<EventEmited>
}

const ListSelect = React.memo(({filters, initialOptions = {}, values, action$}: ListSelectProps) => {

    const onInputChange = (option, query: string) => {
        const {field} = option
        const aux = values.map(ele => ele[field]).filter((ele: string) => ele.toLocaleUpperCase().includes(query.toLocaleUpperCase()))
        const valuesAux = buildOptions(aux)

        setOptions(current => {
            const aux = {...current}
            aux[field] = { ...option, values: valuesAux }
            return aux
        })
    }

    const buildOptions = (values: any[]) => {
        const valuesWithoutRepeats = [...new Set(values)]
        return valuesWithoutRepeats.slice(0, 10).map((ele) => ({ value: ele, label: ele}))
    }

    const onChange = (key, values = []) => {
        setOptionsSelected(current => {
            const aux = {...current}
            aux[key] = []
            
            values.forEach(ele => {
                if(aux) aux[key].push(ele)
            })

            action$.emit({filtersSelected: aux})
            return aux;
        })
    }

    const init = (filters) => { 
        if(!filters) return 
        
        filters.forEach((filter: Filter)  => {
            const {field} = filter
            const aux = values.map(ele => ele[field])
            const valuesAux = buildOptions(aux)
            
            setOptions(current => {
                const aux = {...current}
                aux[field] = { ...filter, values: valuesAux }
                return aux
            })
        })
    }

    const [options, setOptions] = useState({})
    const [optionsSelected, setOptionsSelected] = useState<any>(initialOptions); // TO-DO define type

    useEffect(() => {        
        init(filters)
    }, [])

    action$.useSubscription(({reset}: EventEmited ) => {
        if(reset === true) {
            init(filters)
            setOptionsSelected({})
        }
    });

    return <>
         {
            Object.keys(options)?.map((key, index) => {

                const option = options[key]
                const {label, field, values} = option

                return <WrapperSelect key={index}>
                            <label>{i18n(label)}</label>
                            <Select
                                multi
                                options={values}
                                value={optionsSelected[field]}                                
                                onChange={(value, obj) => {
                                    onChange(key, value)
                                }}
                                onInputChange={(query) => {
                                    onInputChange(option, query)
                                }}
                            />
                        </WrapperSelect> 
            })
        }
    </>
})

const Wrapper = styled.div`
    
`

const WrapperSelect = styled.div`
    margin-bottom: 16px;;
    label{
        color: ${({theme}) => theme['gray-700']};
    }
`

const WrappperActions = styled.div`
    display: flex;
    justify-content: center;

    .dropdown-filter-btn-item{
        flex: 1;
    }

    span, button{
        width: 100%;
    }
`

const RemoveFilters = styled(ButtonWithLoading)`
    border: none;
    outline: none;
    background: none;
    color: ${({theme}) => theme['blue-500']};
    
    &:hover, &:disabled{
        background: none;
        border: none;
        color: ${({theme}) => theme['blue-500']};
    }
`

const BtnLabel = styled.div`
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px !important;
    background-color: ${({theme}) => theme['gray-0']};

    &:hover{ 
        background-color: ${({theme}) => theme['blue-200']};
    }
`

